import { Router } from '@angular/router';

/**
 * The breakpoint the shell already treats as a phone layout — the same query
 * `custom-pagination` collapses at, so the chat and the rest of the app agree on
 * what "mobile" means.
 */
export const PHONE_MEDIA_QUERY = '(max-width: 991.98px)';

/**
 * The slice of `window` this needs, so a test can hand in a fake. `null` means "no window",
 * spelled out — an `undefined` argument would silently pick up the real one via the default.
 */
export type ChatRouteWindow = Pick<Window, 'matchMedia' | 'open'>;

function defaultWindow(): ChatRouteWindow | null {
  return typeof window !== 'undefined' ? window : null;
}

export function isPhoneViewport(win: ChatRouteWindow | null = defaultWindow()): boolean {
  return !!win && win.matchMedia(PHONE_MEDIA_QUERY).matches;
}

/**
 * Opens an in-app route from a chat message's "Open draft" button (Plan 006, B).
 *
 * <p>On a desktop the draft opens in a **new tab**: the person is mid-conversation with the
 * assistant, and navigating away in place loses the transcript they were reading (asked for on
 * 2026-09-30). On a phone there is no room for two tabs and a popup is a worse experience than
 * a page, so the app navigates to the edit form in place — `/accounts-payable/details/:id` is
 * already the edit form on every viewport.
 *
 * <p>Falls back to in-place navigation when the browser refuses the new tab (a popup blocker),
 * so the button always goes somewhere.
 */
export function openChatRoute(
  router: Router,
  route: string,
  win: ChatRouteWindow | null = defaultWindow(),
): void {
  if (!win || isPhoneViewport(win)) {
    router.navigateByUrl(route);
    return;
  }

  // No `noopener`: with it, `window.open` returns null by specification, so the fallback below
  // fired on every click and the draft opened in the new tab *and* in this one (dev,
  // 2026-09-30). The route is same-origin, so the opener reference is harmless, and a null
  // here now means exactly one thing — the browser refused the tab.
  const tab = win.open(route, '_blank');
  if (!tab) router.navigateByUrl(route);
}
